/**
 * ImpersonationBanner — fixed top banner shown when an admin is "logged in
 * as" another user via the Settings → Users → Impersonate action.
 *
 * Detection:
 *   - Decodes the JWT in localStorage.token; if it has an `impersonator_id`
 *     claim, the session is impersonated.
 *   - Falls back to checking localStorage.impersonator_backup_token (set by
 *     the start-impersonation flow), so the banner stays consistent even if
 *     the token has been refreshed.
 *
 * Exit:
 *   1. POST /api/admin/impersonate/exit (audit hook, fire-and-forget).
 *   2. Restore localStorage.token / refresh_token from the backup keys.
 *   3. Hard reload so every cached API state and route guard re-runs as the
 *      original admin user.
 */
import React, { useEffect, useState } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import api from '../../services/api';

const BACKUP_TOKEN_KEY = 'impersonator_backup_token';
const BACKUP_REFRESH_KEY = 'impersonator_backup_refresh';

function decodeJwt(tok) {
  try {
    const payload = JSON.parse(atob(tok.split('.')[1]));
    return payload || null;
  } catch {
    return null;
  }
}

function getImpersonationInfo() {
  const tok = localStorage.getItem('token');
  if (!tok) return null;
  const claims = decodeJwt(tok) || {};
  const hasBackup = !!localStorage.getItem(BACKUP_TOKEN_KEY);
  if (!claims.impersonator_id && !hasBackup) return null;
  return {
    targetUsername: claims.username || `user #${claims.user_id || claims.sub}`,
    targetRole: claims.role || '',
    impersonatorUsername: claims.impersonator_username || '',
    impersonatorId: claims.impersonator_id || null,
  };
}

export default function ImpersonationBanner() {
  const [info, setInfo] = useState(getImpersonationInfo);
  const [exiting, setExiting] = useState(false);

  // Re-check on storage events (e.g., another tab impersonates)
  useEffect(() => {
    const onStorage = () => setInfo(getImpersonationInfo());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!info) return null;

  const exit = async () => {
    setExiting(true);
    try {
      await api.post('/api/admin/impersonate/exit').catch(() => {});
    } finally {
      const backupToken = localStorage.getItem(BACKUP_TOKEN_KEY);
      const backupRefresh = localStorage.getItem(BACKUP_REFRESH_KEY);
      if (backupToken)   localStorage.setItem('token', backupToken);
      if (backupRefresh) localStorage.setItem('refresh_token', backupRefresh);
      localStorage.removeItem(BACKUP_TOKEN_KEY);
      localStorage.removeItem(BACKUP_REFRESH_KEY);
      // Hard navigate home to reset every cached route/state to admin context
      window.location.href = '/';
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(90deg, #b91c1c, #dc2626)',
        color: '#fff',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
        position: 'sticky',
        top: 0,
        zIndex: 5000,
      }}
      role="alert"
    >
      <ShieldAlert size={18} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
        <span>You are impersonating </span>
        <span style={{ background: 'rgba(255,255,255,0.18)', padding: '2px 8px', borderRadius: 6 }}>
          {info.targetUsername}{info.targetRole ? ` · ${info.targetRole}` : ''}
        </span>
        {info.impersonatorUsername && (
          <span style={{ marginLeft: 10, opacity: 0.85, fontWeight: 500 }}>
            as {info.impersonatorUsername}
          </span>
        )}
      </div>
      <button
        onClick={exit}
        disabled={exiting}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.18)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
          padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: exiting ? 'wait' : 'pointer',
        }}
        title="Restore your admin session"
      >
        <LogOut size={14} /> {exiting ? 'Exiting…' : 'Exit Impersonation'}
      </button>
    </div>
  );
}
